/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import React from 'react'
import classNames from 'classnames/bind'
import { RouteComponentProps } from 'react-router-dom'
import { Field } from 'redux-form'
import { ROUTES } from 'constants/routes'
import { Text } from 'hocs/localization'
import Loading from 'apps/Loading'
import { Input, Checkbox } from 'apps/FormFields'
import Button from 'elements/Button'
import LeaseAgreement from 'apps/LeaseAgreement'
import { toCurrency } from 'utils/price'
import { createValidation, createValidator, hasRequired, hasRequiredCheckbox } from 'utils/form'
import { FIELD_NAMES } from './constants'
import * as styles from './Terms.scss'
import logo from 'assets/images/logo.png'

const cx = classNames.bind(styles)

interface IProps extends RouteComponentProps {
  t: Text
  plan: any,
  summary: any,
  handleSubmit: any,
  acceptTerms: () => Promise<any>
  onStartChangePlan: () => void,
  downloadTerms: () => void,
  termsFileUrl: string | null,
  isLoading: boolean,
  submitting: boolean,
  valid: boolean,
}

interface IState {
  isSubmitting: boolean,
  isTermsAccepted: boolean,
}

class Terms extends React.Component<IProps, IState> {
  public state = {
    isSubmitting: false,
    isTermsAccepted: false,
  }

  public requiredFieldValidation = createValidation([
    createValidator(hasRequired, this.props.getTextPath('errors.textField')),
  ])

  public requiredCheckboxValidation = createValidation([
    createValidator(hasRequiredCheckbox, ''),
  ])

  private onAccept = formValue => {
    this.setState({
      isSubmitting: true,
    })

    this.props.acceptTerms(formValue).finally(() => {
      this.setState({
        isSubmitting: false,
      })
    })
  }

  private onChangePlan = () => {
    this.props.history.push(ROUTES.profile.changePlan.root)
  }

  public render () {
    const {
      t,
      plan,
      isLoading,
      submitting,
      valid,
      handleSubmit,
      data,
    } = this.props

    return (
      <div className={styles.topSpace50}>
        <div className={cx('col-md-12', 'text-center', styles.container768)}>
          <div className={styles.description}>{t('description')}</div>
          <div className={styles.selectedPlan}>
            <h2>{t('selectedPlan')}</h2>
          </div>
        </div>

        <div className={cx('col-md-12', styles.container768)}>
          {plan && (
            <div className={styles.Content}>
            <div className={styles.leftImg}><img src={plan.imageUri} alt={plan.name}/></div>
            <div className={styles.rightside}>
              <p><div>{plan.name} {t('plan')}</div>
              <div>{plan.productDescription} {t('printer')}</div>
              <div>{t('with')} <span className={styles.boldText}>{t('unlimited')}</span></div>
              <div><span className={styles.boldText}>{t('automat')}</span></div>
              </p>
              <div>
                <span className={styles.price}> {t('price', { price: toCurrency(plan.price) })} </span>
                <span className={styles.fee}>{t('taxes')}</span>
              </div>
              <h2>
                {!!plan.activationFee && (
                  <div className={styles.fee}>
                    {t('activationFee', { activationFee: toCurrency(plan.activationFee) })}
                  </div>
                )}
              </h2>
              <div className={styles.allRightImgs}>
                <div className={styles.rightImg1}><img src={plan.productImageUri} alt={plan.productDescription}/></div>
                <div className={styles.rightImg2} ><img src={logo} /></div>
              </div>
              <div className={styles.pLink} onClick={this.onChangePlan}>{t('changePlanButton')}</div>
            </div>
          </div>
          )}
          {!plan && (
            <div className={styles.ContentEmpty}>
              <div>{t('noPlanInfo')}</div>
              <div className={cx('text-center', styles.pLink)} onClick={this.onChangePlan}>{t('changePlanButton')}</div>
            </div>
          )}
        </div>
        {isLoading
          ? (<div className={styles.topSpace50}>
              <Loading />
          </div>)
          : (<LeaseAgreement {...data} />)
        }
        <div className={styles.terms}>
          <div className={styles.container1200}>
            <form className={styles.container768}>
              <div className={styles.signForm}>
                <div className={styles.signature}>
                  <div className={styles.companyName}>{data && data.businessName ? data.businessName : ''}</div>
                  <div className={styles.info}>{t('signaturePrompt')}</div>
                  <Field
                    disabled={submitting}
                    type='text'
                    name={FIELD_NAMES.customerSignature}
                    component={Input}
                    validate={this.requiredFieldValidation}
                  />
                  <div className={styles.signatureNote}>{t('signatureNote')}</div>
                  <div className={styles.info}>{data ? data.contractStartDate : '' }</div>
                </div>
                <div className={styles.signature}>
                  <div className={styles.companyName}/>
                  <div className={styles.info}>{t('epsonName')}</div>
                  <Field
                    type='text'
                    component={Input}
                    name={FIELD_NAMES.epsonSignature}
                    placeholder={t('epsonName')}
                    disabled
                  />
                  <div className={styles.signatureNote}/>
                  <div className={styles.info}>{data ? data.contractStartDate : '' }</div>
                </div>
              </div>
              <div className={styles.agreementCheckbox}>
                <Field
                  disabled={submitting}
                  type='text'
                  name={FIELD_NAMES.termsRead}
                  component={Checkbox}
                  label={t('note')}
                  validate={this.requiredCheckboxValidation}
                />
              </div>
              <div className={cx('row', styles.container768)}>
                <div className='col-md-12 textcenter'>
                  <div className={styles.buttonWrapper}>
                    <Button
                      disabled={this.state.isSubmitting || isLoading || submitting || !valid}
                      onClick={handleSubmit(this.onAccept)}
                      fluid
                    >
                      {this.state.isSubmitting ? t('submitting') : t('createButton')}
                    </Button>
                  </div>
                  <div className={cx('row', styles.utc)}>
                    {t('utc')}
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    )
  }
}

export default Terms
